import { Link, useParams } from 'react-router-dom';
import { getGameMeta } from '@shared/data/gamesMeta';
import { ALIEN_INVASION_EXPANSION_ID } from '@shared/data/on-mars/alien-invasion/expansion';
import { perfectRulebooks } from '@shared/data/perfect-quality/generatedRulebooks';
import { getPwaGameBundle } from '../content/gameBundle';
import { useGamePrefs } from '../state/GamePrefsProvider';

type HubTile = {
  key: string;
  label: string;
  hint: string;
  href?: string;
  visible?: boolean;
};

export function GameHubScreen() {
  const { gameId = '' } = useParams();
  const game = getGameMeta(gameId);
  const bundle = getPwaGameBundle(gameId);
  const { readingPercent, resetGame, getPrefs } = useGamePrefs();
  const prefs = getPrefs(gameId);
  const percent = readingPercent(gameId, bundle.manual.chapters.length);
  const isOnMars = gameId === 'on-mars';
  const alienEnabled = prefs.selectedExpansionIds.includes(
    ALIEN_INVASION_EXPANSION_ID
  );

  if (!game) {
    return (
      <div className="empty">
        <h1>Nie znaleziono gry</h1>
        <Link to="/" className="primary-btn" style={{ display: 'inline-block', marginTop: 16 }}>
          Wróć do biblioteki
        </Link>
      </div>
    );
  }

  const tiles: HubTile[] = [
    { key: 'manual', label: 'Pełna instrukcja', hint: 'Rozdziały i treść', href: `/game/${gameId}/manual/0` },
    { key: 'first-game', label: 'Pierwsza gra', hint: 'Krok po kroku', href: `/game/${gameId}/first-game` },
    {
      key: 'guided',
      label: 'Przewodnik interaktywny',
      hint: 'Setup i pierwsza partia',
      href: `/game/${gameId}/guided`,
      visible: isOnMars,
    },
    {
      key: 'turn-helper',
      label: 'Pomoc w turze',
      hint: 'Szybki przewodnik tury',
      href: `/game/${gameId}/guided?step=turn-001`,
      visible: isOnMars,
    },
    { key: 'quick-reference', label: 'Szybka ściąga', hint: 'Przy stole' },
    { key: 'faq', label: 'FAQ', hint: 'Pytania i odpowiedzi' },
    { key: 'glossary', label: 'Słownik', hint: 'Pojęcia' },
    { key: 'scoring', label: 'Punktacja', hint: 'Tabele VP/OP' },
    { key: 'mistakes', label: 'Częste błędy', hint: 'Na co uważać' },
    {
      key: 'perfect-quality',
      label: 'Przewodnik i przykłady',
      hint: 'Perfect-quality',
      visible: Boolean(perfectRulebooks[gameId]),
    },
    {
      key: 'figures-reference',
      label: 'Pionki i figurki',
      hint: 'Tabela figur',
      visible: gameId === 'carcassonne',
    },
    {
      key: 'marine-worlds',
      label: 'Zwierzęta Wodne',
      hint: 'Hub dodatku Marine Worlds',
      visible: gameId === 'ark-nova',
    },
    {
      key: 'ark-nova-special',
      label: 'Ark Nova — specjalne',
      hint: 'Dodatek i tematy',
      visible: gameId === 'ark-nova',
    },
    { key: 'search', label: 'Szukaj', hint: 'W treści gry' },
    { key: 'expansions', label: 'Dodatki / warianty', hint: 'Moduły i ekspansje' },
    {
      key: 'alien-invasion',
      label: 'Alien Invasion',
      hint: prefs.selectedScenarioIds[ALIEN_INVASION_EXPANSION_ID]
        ? `Scenariusz: ${prefs.selectedScenarioIds[ALIEN_INVASION_EXPANSION_ID]}`
        : 'Wymaga włączenia dodatku',
      href: `/game/${gameId}/alien-invasion`,
      visible: isOnMars && alienEnabled,
    },
    { key: 'qr', label: 'Kod QR', hint: 'Udostępnij deep-link' },
  ];

  return (
    <section data-testid="game-hub">
      <p className="eyebrow">Gra</p>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        {game.logoFile ? (
          <img
            src={`/game-logos/${game.logoFile}`}
            alt={game.logoAccessibilityLabel ?? game.title}
            style={{ width: 120, height: 80, objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: 56 }}>{game.icon}</span>
        )}
        <div>
          <h1>{game.title}</h1>
          <p className="subtitle">{game.subtitle}</p>
          <p className="meta">
            {game.players} · {game.playTime} · {bundle.manual.chapters.length}{' '}
            rozdziałów
          </p>
          <p className="meta" data-testid="hub-reading-percent">
            Postęp czytania: {percent}%
            {prefs.lastSectionId ? ` · ostatni: ${prefs.lastSectionId}` : ''}
            {prefs.activeVariantId
              ? ` · wariant: ${prefs.activeVariantId}`
              : ''}
          </p>
        </div>
      </div>

      <div className="hub-grid">
        {tiles
          .filter((tile) => tile.visible !== false)
          .map((tile) => (
            <Link
              key={tile.key}
              className="hub-tile"
              data-testid={`hub-tile-${tile.key}`}
              to={
                tile.href ??
                (tile.key === 'manual'
                  ? `/game/${gameId}/manual/0`
                  : `/game/${gameId}/${tile.key}`)
              }
            >
              {tile.label}
              <span>{tile.hint}</span>
            </Link>
          ))}
      </div>

      <div style={{ marginTop: 24 }}>
        <button
          type="button"
          className="ghost-btn"
          data-testid="reset-progress"
          onClick={() => {
            if (
              typeof window !== 'undefined' &&
              window.confirm(
                'Zresetować postęp, checklisty i wybrane dodatki dla tej gry?'
              )
            ) {
              resetGame(gameId);
            }
          }}
        >
          Resetuj postęp tej gry
        </button>
      </div>
    </section>
  );
}
